<template>
  <div class="dashboard">
    <div class="page-header">
      <h1 class="page-title">
        <i class="fas fa-robot" style="margin-right: 1rem; color: #667eea;"></i>
        Assistance IA
      </h1>
      <button class="btn-primary">
        <i class="fas fa-paper-plane"></i>
        Envoyer
      </button>
    </div>
    
    <!-- KPI Cards with Circular Gauges -->
    <div class="stats-grid">
      <div class="stat-card" v-for="kpi in kpis" :key="kpi.id">
        <div class="card-header">
          <div class="card-icon" :class="kpi.iconClass">
            <i :class="kpi.icon"></i>
          </div>
          <div class="card-info">
            <div class="card-value">{{ formatNumber(kpi.value) }}</div>
            <div class="card-label">{{ kpi.label }}</div>
          </div>
        </div>
        
        <!-- Circular Gauge -->
        <div class="gauge-container" v-if="kpi.showProgress">
          <svg class="circular-gauge" viewBox="0 0 200 200">
            <circle
              cx="100"
              cy="100"
              r="80"
              fill="none"
              stroke="#f0f0f0"
              stroke-width="12"
            />
            <circle
              cx="100"
              cy="100"
              r="80"
              fill="none"
              :stroke="kpi.color"
              stroke-width="12"
              stroke-linecap="round"
              :stroke-dasharray="`${2 * Math.PI * 80}`"
              :stroke-dashoffset="(1 - (kpi.percentage / 100)) * (2 * Math.PI * 80)"
              class="gauge-progress"
            />
            <text x="100" y="100" text-anchor="middle" dominant-baseline="middle" class="gauge-text">
              {{ kpi.percentage }}%
            </text>
          </svg>
        </div>

        <div class="progress-info">
          <div class="progress-item">
            <i class="fas fa-check-circle" style="color: #48bb78;"></i>
            <span>{{ kpi.success }}</span>
          </div>
          <div class="progress-item">
            <i class="fas fa-clock" style="color: #ed8936;"></i>
            <span>{{ kpi.pending }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="charts-grid">
      <div class="chart-card">
        <div class="chart-header">
          <i class="fas fa-chart-pie"></i>
          <h3>Répartition des statuts (emails)</h3>
        </div>
        <div class="chart-container">
          <DoughnutChart :data="statusChartData" :options="doughnutOptions" />
        </div>
        <div class="chart-legend">
          <div class="legend-item" v-for="(item, index) in statusLegend" :key="index">
            <span class="legend-color" :style="{ backgroundColor: item.color }"></span>
            <span class="legend-label">{{ item.label }}</span>
          </div>
        </div>
      </div>
      
      <div class="chart-card">
        <div class="chart-header">
          <i class="fas fa-chart-pie"></i>
          <h3>Emails sur 7 jours</h3>
        </div>
        <div class="chart-container">
          <DoughnutChart :data="emails7dChartData" :options="doughnutOptions" />
        </div>
        <div class="chart-legend">
          <div class="legend-item" v-for="(item, index) in emails7dLegend" :key="index">
            <span class="legend-color" :style="{ backgroundColor: item.color }"></span>
            <span class="legend-label">{{ item.label }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { Doughnut as DoughnutChart } from 'vue-chartjs'
import {
  Chart as ChartJS,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js'
import { useDashboardStore } from '../stores/dashboard'

// Enregistrer les composants Chart.js
ChartJS.register(
  ArcElement,
  Title,
  Tooltip,
  Legend
)

export default {
  name: 'Dashboard',
  components: {
    DoughnutChart
  },
  setup() {
    const dashboardStore = useDashboardStore()
    const loading = ref(true)

    const emailsStats = computed(() => dashboardStore.stats?.emails || { total: 0, processed: 0, pending: 0, error: 0 })
    const commandesStats = computed(() => dashboardStore.stats?.commandes || { total: 0, validated: 0, pending: 0 })

    const processedPct = computed(() => emailsStats.value.total ? Math.round((emailsStats.value.processed / emailsStats.value.total) * 100) : 0)
    const commandesPct = computed(() => commandesStats.value.total ? Math.round((commandesStats.value.validated / commandesStats.value.total) * 100) : 0)

    const kpis = computed(() => [
      {
        id: 'messages',
        label: 'MESSAGES',
        value: emailsStats.value.total,
        icon: 'fas fa-envelope',
        iconClass: 'messages',
        color: '#667eea',
        percentage: processedPct.value,
        success: `${emailsStats.value.processed} traités`,
        pending: `${emailsStats.value.pending} à traiter`,
        showProgress: true
      },
      {
        id: 'analyse',
        label: 'ANALYSE IA',
        value: emailsStats.value.processed, // approximation: emails traités
        icon: 'fas fa-brain',
        iconClass: 'analyse',
        color: '#48bb78',
        percentage: processedPct.value,
        success: `${emailsStats.value.processed} analysés`,
        pending: `${Math.max(emailsStats.value.total - emailsStats.value.processed, 0)} à analyser`,
        showProgress: true,
        confidence: null
      },
      {
        id: 'commandes',
        label: 'COMMANDES',
        value: commandesStats.value.total,
        icon: 'fas fa-shopping-cart',
        iconClass: 'commandes',
        color: '#ed8936',
        percentage: commandesPct.value,
        success: `${commandesStats.value.validated} traitées`,
        pending: `${Math.max(commandesStats.value.total - commandesStats.value.validated, 0)} à traiter`,
        showProgress: true
      },
      {
        id: 'anomalies',
        label: 'ANOMALIES',
        value: emailsStats.value.error,
        icon: 'fas fa-exclamation-triangle',
        iconClass: 'anomalies',
        color: '#e53e3e',
        percentage: 100,
        success: `Pas d'anomalie majeure`,
        pending: `${emailsStats.value.error} en erreur`,
        showProgress: false
      }
    ])

    const chartData = computed(() => dashboardStore.chartData || { emailsOverTime: { labels: [], data: [] }, statusDistribution: [0,0,0,0] })

    const statusChartData = computed(() => ({
      labels: ['Traités', 'En attente', 'Erreur', 'Validés'],
      datasets: [{
        data: chartData.value.statusDistribution || [emailsStats.value.processed, emailsStats.value.pending, emailsStats.value.error, commandesStats.value.validated],
        backgroundColor: ['#48bb78', '#ed8936', '#e53e3e', '#3182ce'],
        borderWidth: 0
      }]
    }))

    const statusLegend = [
      { label: 'Traités', color: '#48bb78' },
      { label: 'En attente', color: '#ed8936' },
      { label: 'Erreur', color: '#e53e3e' },
      { label: 'Validés', color: '#3182ce' }
    ]

    const emails7dChartData = computed(() => ({
      labels: chartData.value.emailsOverTime?.labels || [],
      datasets: [{
        data: chartData.value.emailsOverTime?.data || [],
        backgroundColor: (chartData.value.emailsOverTime?.data || []).map((_, i) => {
          const palette = ['#4ecdc4', '#45b7d1', '#f9ca24', '#6c5ce7', '#fd79a8', '#ff6b9d', '#22c55e']
          return palette[i % palette.length]
        }),
        borderWidth: 0
      }]
    }))

    const emails7dLegend = computed(() => (chartData.value.emailsOverTime?.labels || []).map((label, i) => {
      const palette = ['#4ecdc4', '#45b7d1', '#f9ca24', '#6c5ce7', '#fd79a8', '#ff6b9d', '#22c55e']
      return { label, color: palette[i % palette.length] }
    }))

    const doughnutOptions = {
      responsive: true,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: true }
      },
      cutout: '65%'
    }

    function formatNumber(num) {
      if (num === null || num === undefined) return '—'
      if (num < 1000) return num
      if (num < 1000000) return `${(num / 1000).toFixed(1)}K`
      return `${(num / 1000000).toFixed(1)}M`
    }

    onMounted(async () => {
      try {
        await dashboardStore.loadDashboardData()
      } catch (error) {
        console.error('Erreur lors du chargement du dashboard:', error)
      } finally {
        loading.value = false
      }
    })
    
    return {
      kpis,
      statusChartData,
      emails7dChartData,
      statusLegend,
      emails7dLegend,
      doughnutOptions,
      formatNumber,
      loading
    }
  }
}
</script>

<style scoped>
.dashboard {
  padding: 2rem;
  background-color: #f8fafc;
  min-height: 100vh;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
  display: flex;
  align-items: center;
}

.btn-primary {
  background: #667eea;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary:hover {
  background: #5a67d8;
  transform: translateY(-1px);
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.card-header {
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
}

.card-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  font-size: 1.2rem;
}

.card-icon.messages {
  background: rgba(102, 126, 234, 0.1);
  color: #667eea;
}

.card-icon.analyse {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.card-icon.commandes {
  background: rgba(102, 126, 234, 0.1);
  color: #667eea;
}

.card-icon.anomalies {
  background: rgba(251, 146, 60, 0.1);
  color: #fb923c;
}

.card-info {
  flex: 1;
}

.card-value {
  font-size: 2rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 0.25rem;
}

.card-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #718096;
  letter-spacing: 0.05em;
}

.gauge-container {
  display: flex;
  justify-content: center;
  margin: 1.5rem 0;
}

.circular-gauge {
  width: 120px;
  height: 120px;
}

.gauge-progress {
  transition: stroke-dashoffset 1s ease-in-out;
}

.gauge-text {
  fill: #2d3748;
  font-weight: 700;
  font-size: 18px;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
}

.progress-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #4a5568;
  font-size: 0.9rem;
}

.charts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.5rem;
}

.chart-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.chart-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.chart-container {
  position: relative;
}

.chart-legend {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem 1rem;
  margin-top: 1rem;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 3px;
  flex-shrink: 0;
}

/* Le style global `.page-header .page-title` (titre blanc pour fond violet)
   rendait le titre illisible sur la carte blanche : on réaffirme la couleur. */
.page-header .page-title {
  color: #2d3748;
  text-shadow: none;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  text-align: left;
  min-width: 0;
}

.stats-grid {
  grid-template-columns: repeat(auto-fit, minmax(min(300px, 100%), 1fr));
}

.charts-grid {
  grid-template-columns: repeat(auto-fit, minmax(min(320px, 100%), 1fr));
}

.stat-card,
.chart-card,
.card-info {
  min-width: 0;
}

.legend-label {
  min-width: 0;
  overflow-wrap: anywhere;
}

@media (max-width: 991.98px) {
  .dashboard {
    padding: 0;
    min-height: 0;
    background: transparent;
  }

  .page-header {
    margin-bottom: 1.25rem;
    padding: 1rem 1.25rem;
    gap: 0.75rem;
  }

  .stats-grid {
    gap: 1rem;
    margin-bottom: 1.25rem;
  }

  .charts-grid {
    gap: 1rem;
  }

  /* Doughnuts : on plafonne la taille pour éviter des anneaux géants */
  .chart-container {
    max-width: 280px;
    margin: 0 auto;
  }
}

@media (max-width: 575.98px) {
  .page-header {
    flex-direction: column;
    align-items: stretch;
  }

  .page-header .page-title {
    font-size: 1.3rem;
  }

  .page-header .page-title i {
    margin-right: 0.75rem !important;
  }

  .page-header .btn-primary {
    justify-content: center;
    min-height: 44px;
  }

  /* KPI : 2 colonnes compactes */
  .stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
  }

  .stat-card {
    padding: 1rem;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }

  .card-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    margin-right: 0;
    font-size: 1rem;
  }

  .card-value {
    font-size: 1.5rem;
  }

  .card-label {
    font-size: 0.75rem;
  }

  .gauge-container {
    margin: 0.5rem 0;
  }

  .circular-gauge {
    width: 84px;
    height: 84px;
  }

  .progress-info {
    flex-direction: column;
    gap: 0.35rem;
    margin-top: 0.5rem;
  }

  .progress-item {
    font-size: 0.8rem;
    align-items: flex-start;
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .progress-item i {
    margin-top: 0.15rem;
  }

  .chart-card {
    padding: 1rem;
  }

  .chart-header h3 {
    font-size: 1.1rem;
    margin: 0;
  }

  .chart-container {
    max-width: 220px;
  }
}

@media (max-width: 339.98px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
}
</style>